import React, { useMemo } from "react";

/**
 * HeatmapBI - Visualización de heatmap para meses vs tiendas.
 * Implementado como grid de celdas coloreadas con intensidad proporcional al valor.
 *
 * Props:
 * - data: Array de { mes: string, tienda: string, ventas: number }
 * - tiendas: string[] - lista de tiendas (columnas)
 * - title: string (opcional)
 */
export function HeatmapBI({ data = [], tiendas = [], title }) {
  const { meses, matrix, minValue, maxValue } = useMemo(() => {
    // Extraer meses únicos preservando el orden original
    const mesesSet = [];
    const mesesSeen = new Set();
    data.forEach((d) => {
      if (!mesesSeen.has(d.mes)) {
        mesesSeen.add(d.mes);
        mesesSet.push(d.mes);
      }
    });

    // Construir la matriz de valores mes x tienda
    const matrixMap = {};
    let min = Infinity;
    let max = -Infinity;

    data.forEach((d) => {
      const key = `${d.mes}__${d.tienda}`;
      matrixMap[key] = d.ventas;
      if (d.ventas < min) min = d.ventas;
      if (d.ventas > max) max = d.ventas;
    });

    // Si no hay datos, usar 0 para ambos
    if (min === Infinity) min = 0;
    if (max === -Infinity) max = 0;

    return {
      meses: mesesSet,
      matrix: matrixMap,
      minValue: min,
      maxValue: max,
    };
  }, [data]);

  const getIntensity = (value) => {
    if (maxValue === minValue) return 0.3;
    return 0.1 + ((value - minValue) / (maxValue - minValue)) * 0.9;
  };

  const getCellColor = (value) => {
    const intensity = getIntensity(value);
    // Blue scale: from light blue to dark blue
    const r = Math.round(219 - intensity * 160);
    const g = Math.round(234 - intensity * 130);
    const b = Math.round(254 - intensity * 30);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const getTextColor = (value) => {
    const intensity = getIntensity(value);
    return intensity > 0.6 ? "#FFFFFF" : "#1E3A5F";
  };

  const formatValue = (value) => {
    if (value === undefined || value === null) return "—";
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toLocaleString("es-CR");
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      {title && (
        <p className="font-semibold text-gray-700 mb-4">{title}</p>
      )}

      {data.length === 0 ? (
        <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
          Sin datos disponibles
        </div>
      ) : (
        <>
          {/* Grid */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 sticky left-0 bg-white">
                    Mes
                  </th>
                  {tiendas.map((tienda) => (
                    <th
                      key={tienda}
                      className="px-2 py-1 text-center text-xs font-medium text-gray-500"
                    >
                      {tienda}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {meses.map((mes) => (
                  <tr key={mes}>
                    <td className="px-2 py-1 text-xs text-gray-600 font-medium whitespace-nowrap sticky left-0 bg-white">
                      {mes}
                    </td>
                    {tiendas.map((tienda) => {
                      const key = `${mes}__${tienda}`;
                      const value = matrix[key] ?? 0;
                      return (
                        <td
                          key={key}
                          className="px-1 py-1 text-center"
                        >
                          <div
                            className="rounded px-2 py-2 text-xs font-medium min-w-[60px]"
                            style={{
                              backgroundColor: getCellColor(value),
                              color: getTextColor(value),
                            }}
                            title={`${mes} - ${tienda}: ₡${Number(value).toLocaleString("es-CR")}`}
                          >
                            {formatValue(value)}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Color Legend */}
          <div className="mt-4 flex items-center gap-2 text-xs text-gray-500">
            <span>Menor</span>
            <div className="flex h-4 rounded overflow-hidden">
              {[0, 0.2, 0.4, 0.6, 0.8, 1].map((t) => {
                const intensity = 0.1 + t * 0.9;
                const r = Math.round(219 - intensity * 160);
                const g = Math.round(234 - intensity * 130);
                const b = Math.round(254 - intensity * 30);
                return (
                  <div
                    key={t}
                    className="w-8 h-4"
                    style={{ backgroundColor: `rgb(${r}, ${g}, ${b})` }}
                  />
                );
              })}
            </div>
            <span>Mayor</span>
          </div>
        </>
      )}
    </div>
  );
}

export default HeatmapBI;
